import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Bird, Smartphone, Monitor, Tablet, Download, Check, ChevronRight, ArrowLeft, Apple, Chrome } from "lucide-react";

function getPlatform() {
  const ua = navigator.userAgent || "";
  if (/android/i.test(ua)) return "android";
  if (/iPad|iPhone|iPod/.test(ua)) return "ios";
  return "desktop";
}

export default function InstallApp() {
  const nav = useNavigate();
  const [platform, setPlatform] = useState(getPlatform);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener("beforeinstallprompt", handler);
    window.addEventListener("appinstalled", () => setInstalled(true));
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const result = await deferredPrompt.userChoice;
    if (result.outcome === "accepted") setInstalled(true);
    setDeferredPrompt(null);
  };

  const platforms = [
    { id: "android", label: "Android", icon: Smartphone, color: "bg-green-500" },
    { id: "ios", label: "iOS", icon: Apple, color: "bg-slate-900" },
    { id: "desktop", label: "Desktop", icon: Monitor, color: "bg-blue-600" },
  ];

  const instructions = {
    android: [
      { step: 1, text: "Tap the browser menu (three dots) at the top-right" },
      { step: 2, text: 'Select "Add to Home Screen" or "Install App"' },
      { step: 3, text: 'Tap "Install" on the confirmation popup' },
      { step: 4, text: "Pigeon Supply icon appears on your home screen" },
    ],
    ios: [
      { step: 1, text: "Open this page in Safari (required for iOS)" },
      { step: 2, text: "Tap the Share button (square with arrow) at the bottom" },
      { step: 3, text: 'Scroll down and tap "Add to Home Screen"' },
      { step: 4, text: 'Tap "Add" — Pigeon Supply appears on your home screen' },
    ],
    desktop: [
      { step: 1, text: "Look for the install icon in your browser's address bar" },
      { step: 2, text: 'Or click the button below to install directly' },
      { step: 3, text: "Pigeon Supply opens as a standalone app window" },
      { step: 4, text: "Access it from your taskbar or app launcher" },
    ],
  };

  return (
    <div className="min-h-[100dvh] bg-white flex flex-col" data-testid="install-page">
      <header className="flex items-center justify-between px-6 py-5 border-b border-slate-200">
        <div className="flex items-center gap-3 cursor-pointer" onClick={() => nav("/landing")}>
          <div className="w-10 h-10 bg-lime-500 rounded-xl flex items-center justify-center shadow-md">
            <Bird className="w-5 h-5 text-slate-950" strokeWidth={2} />
          </div>
          <div>
            <h1 className="text-lg font-black tracking-tighter uppercase text-slate-900">Pigeon Supply</h1>
            <p className="text-[10px] font-bold tracking-widest uppercase text-slate-400">Install App</p>
          </div>
        </div>
        <button onClick={() => nav(-1)} className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-500 rounded-xl text-xs font-bold flex items-center gap-2" data-testid="back-btn">
          <ArrowLeft className="w-3.5 h-3.5" /> Back
        </button>
      </header>

      <main className="flex-1 flex items-start justify-center p-6 pt-10">
        <div className="w-full max-w-lg">
          <div className="text-center mb-10">
            <div className="w-16 h-16 bg-lime-50 border border-lime-300 rounded-xl flex items-center justify-center mx-auto mb-5">
              <Download className="w-8 h-8 text-lime-500" />
            </div>
            <h2 className="text-3xl font-black tracking-tighter uppercase text-slate-900 mb-2">Install Pigeon Supply</h2>
            <p className="text-sm text-slate-400 font-medium">Works offline. Fast. Native feel on any device.</p>
          </div>

          {installed && (
            <div className="p-5 bg-lime-50 border border-lime-300 rounded-xl mb-6 flex items-center gap-3 animate-fadeIn" data-testid="installed-badge">
              <Check className="w-6 h-6 text-lime-600" />
              <div>
                <p className="text-sm font-bold text-lime-700">App Installed</p>
                <p className="text-xs text-lime-600">Find Pigeon Supply on your home screen or app launcher.</p>
              </div>
            </div>
          )}

          {/* Platform Picker */}
          <div className="mb-8" data-testid="platform-picker">
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-3">Your Device</p>
            <div className="grid grid-cols-3 gap-2">
              {platforms.map((p) => {
                const Icon = p.icon;
                return (
                  <button
                    key={p.id}
                    data-testid={`platform-${p.id}`}
                    onClick={() => setPlatform(p.id)}
                    className={`p-4 rounded-xl border-2 text-center transition-all ${
                      platform === p.id
                        ? "border-lime-400 bg-lime-50 shadow-sm"
                        : "border-slate-200 bg-white hover:border-slate-300"
                    }`}
                  >
                    <div className={`w-10 h-10 ${platform === p.id ? p.color : "bg-slate-100"} rounded-xl flex items-center justify-center mx-auto mb-2 ${platform === p.id ? "text-white" : "text-slate-400"}`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <span className={`text-xs font-bold uppercase tracking-tight ${platform === p.id ? "text-slate-900" : "text-slate-400"}`}>{p.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* One-Click Install (Chrome/Edge on Android & Desktop) */}
          {deferredPrompt && (platform === "android" || platform === "desktop") && (
            <button
              data-testid="one-click-install-btn"
              onClick={handleInstall}
              className="w-full py-5 bg-lime-500 hover:bg-lime-400 text-slate-950 rounded-xl text-sm font-black uppercase flex items-center justify-center gap-2 shadow-lg mb-6 active:scale-[0.98] transition-all"
            >
              <Download className="w-5 h-5" /> Install Now
            </button>
          )}

          {/* Step-by-step Instructions */}
          <div className="p-5 bg-slate-50 border border-slate-200 rounded-xl" data-testid="install-instructions">
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-4">
              {deferredPrompt && (platform === "android" || platform === "desktop") ? "Or install manually" : "How to install"}
            </p>
            <div className="space-y-4">
              {instructions[platform].map((s) => (
                <div key={s.step} className="flex items-start gap-3">
                  <div className="w-7 h-7 bg-lime-500 text-slate-950 rounded-lg flex items-center justify-center shrink-0 text-xs font-black">{s.step}</div>
                  <p className="text-sm text-slate-700 font-medium pt-0.5">{s.text}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Open App */}
          <button
            data-testid="open-app-btn"
            onClick={() => nav("/auth")}
            className="w-full mt-6 py-4 bg-slate-900 hover:bg-slate-800 text-white rounded-xl text-sm font-black uppercase flex items-center justify-center gap-2 transition-all"
          >
            Open Pigeon Supply <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </main>
    </div>
  );
}
